import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LINKS, NAV_ITEMS, TAGLINE_PRIMARY, TAGLINE_SECONDARY, shortCa, copyCa } from '@/data/content';

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
  </svg>
);

export default function Footer() {
  const [caCopied, setCaCopied] = useState(false);

  return (
    <footer
      data-testid="footer"
      className="relative z-10 border-t border-electric/15 bg-navy2/60"
      style={{ backdropFilter: 'blur(10px)' }}
    >
      <div className="mx-auto max-w-[1400px] px-6 py-16 sm:px-10">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <motion.img
              src="/assets/arctoons-logo-opt.webp"
              alt="ARC TOONS logo"
              loading="lazy"
              className="h-16 w-16 object-contain"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />
            <p className="mt-5 font-display text-lg font-extrabold uppercase tracking-tight text-snow" data-testid="footer-brand">
              ARC TOONS
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-electric" data-testid="footer-tagline">
              {TAGLINE_PRIMARY}
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-snow/40">
              {TAGLINE_SECONDARY}
            </p>
            <button
              onClick={async () => setCaCopied(await copyCa())}
              title="Copy contract address"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-dashed border-electric/40 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-electric/80 transition-colors duration-200 hover:text-electric"
              data-testid="footer-ca-chip"
            >
              CA: {caCopied ? 'COPIED!' : shortCa()}
            </button>
          </div>

          <nav aria-label="Footer">
            <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-snow/40">EXPLORE</p>
            <ul className="mt-5 flex flex-col gap-3">
              {NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    data-testid={`footer-${item.id}-link`}
                    className="font-display text-xs font-bold uppercase tracking-[0.2em] text-snow/65 transition-colors duration-200 hover:text-electric"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.35em] text-snow/40">MARKET</p>
            <ul className="mt-5 flex flex-col gap-3">
              <li>
                <a
                  href={LINKS.buy}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-buy-link"
                  className="font-display text-xs font-bold uppercase tracking-[0.2em] text-gold transition-colors duration-200 hover:text-gold/80"
                >
                  BUY — ARGUS.WORLD
                </a>
              </li>
              <li>
                <a
                  href={LINKS.chart}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-chart-link"
                  className="font-display text-xs font-bold uppercase tracking-[0.2em] text-snow/65 transition-colors duration-200 hover:text-electric"
                >
                  CHART — DEXSCREENER
                </a>
              </li>
              <li>
                <a
                  href={LINKS.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-x-link"
                  className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-snow/65 transition-colors duration-200 hover:text-electric"
                >
                  <XIcon className="h-3 w-3" /> X — @ARC_TOONS
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-electric/10 pt-8 sm:flex-row">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-snow/40" data-testid="footer-copyright">
            © 2026 ARC TOONS
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="ml-4 text-snow/25 transition-colors duration-200 hover:text-electric"
            >
              ADMIN
            </Link>
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-snow/30">
            {TAGLINE_PRIMARY}
          </p>
        </div>
      </div>
    </footer>
  );
}
